import { useAuth0 } from "@auth0/auth0-react";
import { ThemeProvider } from "@material-ui/core/styles";
import axios from "axios";
import { useRef } from "react";
import { useDispatch } from "react-redux";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";
import "./App.css";
import NavBar from "./Components/Navigation/NavBar";
import { QueryProvider } from "./Helpers/queryProvider";
import ABTestingPage from "./Pages/ABTestingPage/ABTestingPage";
import ABTestClientDetailsPage from "./Pages/ABTestingPage/ABTestClientDetailsPage";
import BaseClientDetailsPage from "./Pages/BaseClientManagementPage/BaseClientDetailsPage";
import BaseClientManagementPage from "./Pages/BaseClientManagementPage/BaseClientManagementPage";
import BillingReportsDetailed from "./Pages/BillingReportsPage/BillingReportsDetailed";
import BillingReportsPage from "./Pages/BillingReportsPage/BillingReportsPage";
import FeaturesEnabledPage from "./Pages/FeaturesEnabledPage/FeaturesEnabledPage";
import ClientManagementPage from "./Pages/ClientManagement/ClientListPage";
import ClientTabs from "./Pages/ClientManagement/ClientTabs";
import EmailTemplateDetailed from "./Pages/EmailTemplates/EmailTemplateDetailed";
import EmailTemplatesPage from "./Pages/EmailTemplates/EmailTemplatesPage";
import GoogleAccountsPage from "./Pages/GoogleAccountsPage/GoogleAccounts";
import GoogleAccountsDetailed from "./Pages/GoogleAccountsPage/GoogleAccountsDetailed";
import HereDotComPageDetailed from "./Pages/HereDotComPage/HereDotComDetailed";
import HereDotComPage from "./Pages/HereDotComPage/HereDotComPage";
import AtsApiDetailed from "./Pages/InboundFeedsPage/AtsApiDetailed";
import InboundFeedDetailed from "./Pages/InboundFeedsPage/InboundFeedDetailed";
import InboundTabs from "./Pages/InboundFeedsPage/InboundTabs";
import OutboundFeedsTab from "./Pages/OutboundFeedsPage/FeedsTabView";
import OutboundFeedsDetailed from "./Pages/OutboundFeedsPage/OutboundFeedsDetailed";
import OutboundFeedTemplateDetailed from "./Pages/OutboundFeedsPage/TemplateDetailed";
import ProfilePage from "./Pages/ProfilePage/ProfilePage";
import UserTabView from "./Pages/UserManagementDetailPage/UserTabView";
import UserManagementPage from "./Pages/UserManagementPage/UserManagementPage";
import BillingTiersPage from "./Pages/BillingTiersPage/BillingTiersPage";
import BillingTiersDetailPage from "./Pages/BillingTiersDetailPage/BillingTiersDetailPage";
import JobBoardScrapesPage from "./Pages/JobBoardScrapesPage/JobBoardScrapesPage";
import { updateInformation } from "./State/Slices/UserInformationSlice";
import theme from "./theme";

function App() {
  const { isLoading, loginWithRedirect, isAuthenticated, error, user, getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const interceptorsRegistered = useRef(false);

  if (isLoading) {
    return <div>loading...</div>;
  }
  if (error) {
    return <div>Oops... {error.message}</div>;
  }

  if (isAuthenticated) {
    // Register the auth + 401 interceptors once (ref-guarded) in the render body,
    // so they're active before any child page's mount effect fires a request.
    if (!interceptorsRegistered.current) {
      interceptorsRegistered.current = true;

      axios.interceptors.request.use(async function (config) {
        const accessToken = await getAccessTokenSilently();
        config.headers.Authorization = `Bearer ${accessToken}`;
        if (user?.email) {
          config.headers["user"] = user.email;
        }
        return config;
      });

      axios.interceptors.response.use(
        function (response) {
          return response;
        },
        function (error) {
          if (error.response !== undefined && error.response.status === 401)
            window.location.href = import.meta.env.VITE_ACTIVATE_PORTAL;
          return Promise.reject(error);
        }
      );
    }

    dispatch(updateInformation(user));

    return (
      <QueryProvider>
        <ThemeProvider theme={theme}>
          <Router>
            <NavBar />
            <Switch>
              <Route path="/" exact component={BaseClientManagementPage} />
              <Route path="/baseclientmanagement" exact component={BaseClientManagementPage} />
              <Route path="/baseclientmanagement/baseclientdetails" exact component={BaseClientDetailsPage} />
              <Route path="/careersclientmanagement" exact component={ClientManagementPage} />
              <Route path="/careersclientmanagement/careersclientdetails" exact component={ClientTabs} />
              <Route path="/usermanagement" exact component={UserManagementPage} />
              <Route path="/usermanagement/userinfo" exact component={UserTabView} />
              <Route path="/billingreports" exact component={BillingReportsPage} />
              <Route path="/billingreports/billingdetails" component={BillingReportsDetailed} />
              <Route path="/heredotcom" exact component={HereDotComPage} />
              <Route path="/heredotcom/heredotcomdetailed" exact component={HereDotComPageDetailed} />
              <Route path="/billingtiers" exact component={BillingTiersPage} />
              <Route path="/billingtiers/billingtiersdetailed" exact component={BillingTiersDetailPage} />
              <Route path="/inboundfeeds" exact component={InboundTabs} />
              <Route path="/inboundfeeds/inboundfeeddetailed" exact component={InboundFeedDetailed} />
              <Route path="/inboundfeeds/atsapidetailed" exact component={AtsApiDetailed} />
              <Route path="/googleaccounts" exact component={GoogleAccountsPage} />
              <Route path="/googleaccounts/googleaccountsdetailed" exact component={GoogleAccountsDetailed} />
              <Route path="/emailtemplates" exact component={EmailTemplatesPage} />
              <Route path="/emailtemplates/emailtemplatedetailed" exact component={EmailTemplateDetailed} />
              <Route path="/outboundfeeds" exact component={OutboundFeedsTab} />
              <Route
                path="/outboundfeeds/outboundtemplatedetailed"
                exact
                component={OutboundFeedTemplateDetailed}
              />
              <Route path="/outboundfeeds/outboundfeeddetailed" exact component={OutboundFeedsDetailed} />
              <Route path="/profile" exact component={ProfilePage} />
              <Route path="/featuresenabled" exact component={FeaturesEnabledPage} />
              <Route path="/abtesting" exact component={ABTestingPage} />
              <Route path="/abtesting/:clientId" exact component={ABTestClientDetailsPage} />
              <Route path="/jobboardscrapes" exact component={JobBoardScrapesPage} />
            </Switch>
          </Router>
        </ThemeProvider>
      </QueryProvider>
    );
  } else {
    loginWithRedirect();
  }
}

export default App;
